// Contextual, non-shaming motivation shown after an incorrect attempt. Calm tone
// on the first miss, warmer/encouraging on the second. Fade-in; additive — it
// renders above the existing hint/support and changes no answer logic.
import { useMemo } from 'react';
import { pickMotivation, stageFor } from '@/content/motivation';

interface Props {
  /** Upcoming attempt index (2 after first miss, 3 after second). */
  attemptIndex: number;
  topicName?: string | null;
  /** Stable key so the message doesn't reshuffle on unrelated re-renders. */
  questionId: string;
}

export function MotivationBanner({ attemptIndex, topicName, questionId }: Props) {
  const stage = stageFor(attemptIndex);
  // Recompute only when the question or stage changes.
  const msg = useMemo(
    () => pickMotivation({ attemptIndex, topicName }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [questionId, stage],
  );

  const second = stage === 'second';
  const palette = second
    ? { bg: 'var(--green-bg, #E8F5E9)', border: 'var(--green-fill, #A5D6A7)', text: 'var(--green-text, #2E7D32)' }
    : { bg: 'var(--blue-bg, #E3F2FD)', border: 'var(--blue-fill, #90CAF9)', text: 'var(--blue-text, #1565C0)' };

  return (
    <div
      role="status"
      aria-live="polite"
      className="mb-4 rounded-xl border p-3 cwy-fade-in"
      style={{ background: palette.bg, borderColor: palette.border }}
    >
      <p className="text-sm font-bold" style={{ color: palette.text }}>
        {second ? '💪 ' : '🌱 '}{msg.headline}
      </p>
      <p className="mt-0.5 text-sm" style={{ color: palette.text, opacity: 0.92 }}>{msg.body}</p>
      <style>{`@keyframes cwyFadeIn{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}}.cwy-fade-in{animation:cwyFadeIn 220ms ease both}@media (prefers-reduced-motion: reduce){.cwy-fade-in{animation:none}}`}</style>
    </div>
  );
}
